import { useState, useEffect, useCallback } from 'react';
import { FabrikUser } from '../../../types';
import userDataService from '../../../services/data/userService';
import { getUsers, getUsersPagination, isLoadingAllUsers, isUsersDataStale } from '../../../services/data/selectors';

interface UseUsersOptions {
  includeUsageData?: boolean;
  membersOnly?: boolean;
}

interface UseUsersResult {
  users: FabrikUser[];
  allUsers: FabrikUser[];
  isLoading: boolean;
  isBackgroundLoading: boolean;
  isLoadingAllUsers: boolean;
  error: string | null;
  refresh: () => void;
  currentPage: number;
  totalPages: number;
  hasMore: boolean;
  loadMore: () => void;
  goToPage: (page: number) => void;
  setFilter: (email?: string, userId?: string, membersOnly?: boolean) => void;
}

const useUsers = (
  token: string | null, 
  initialEmail?: string, 
  initialUserId?: string,
  options: UseUsersOptions = { includeUsageData: true, membersOnly: false }
): UseUsersResult => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1); // Local state for current page

  // Set token in the data service
  useEffect(() => {
    userDataService.setToken(token);
  }, [token]);
  
  // Set initial filters if provided
  useEffect(() => {
    if (initialEmail || initialUserId) {
      userDataService.setFilters({
        email: initialEmail || null,
        userId: initialUserId || null,
        page: 1,
        membersOnly: options.membersOnly,
        includeUsageData: options.includeUsageData
      });
    }
  }, [initialEmail, initialUserId, options.membersOnly, options.includeUsageData]);
  
  // Load data with SWR pattern
  const loadData = useCallback(async (resetPage: boolean = false, isSearch: boolean = false, forceRefresh: boolean = false) => {
    if (!token) return;
    
    // Check if data is stale before loading
    const isStale = isUsersDataStale();
    
    // Skip loading if data is not stale and not forcing refresh
    if (!isStale && !forceRefresh && !isSearch && !resetPage) {
      return;
    }
    
    setIsLoading(true);
    try {
      if (resetPage || isSearch) {
        await userDataService.refreshAllData();
        setCurrentPage(1); // Reset to page 1 when data is refreshed
      }
      // For pagination, we don't need to fetch anything - it's client-side now
      // The users are already in the store from lazy loading
      
      setError(null);
    } catch (err) {
      console.error('Error loading users:', err);
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Background prefetch for better UX
  const prefetchAllUsers = useCallback(async () => {
    if (!token) return;
    
    try {
      setIsBackgroundLoading(true);
      
      // Get current pagination info
      const pagination = getUsersPagination();
      
      // Prefetch ALL remaining pages in background - accumulate users
      for (let page = pagination.currentPage + 1; page <= pagination.totalPages; page++) {
        const currentFilters = userDataService.getFilters();
        userDataService.setFilters({ ...currentFilters, page });
        await userDataService.fetchUsers(); // Always accumulates
        
        // Check how many users we have now
        const currentUsers = getUsers();
      }
      
      // Reset to original page
      const originalFilters = userDataService.getFilters();
      userDataService.setFilters({ ...originalFilters, page: pagination.currentPage });
    } catch (error) {
      // Silent failure for background prefetch
    } finally {
      setIsBackgroundLoading(false);
    }
  }, [token]);

  // Initial data loading
  useEffect(() => {
    if (!token) return;
    
    loadData(true, false).then(() => {
      // Start background prefetch after initial load
      setTimeout(() => prefetchAllUsers(), 1000);
    });
  }, [token, loadData, prefetchAllUsers]);
  
  // Set up revalidation trigger: only on reconnect (no focus refetch)
  useEffect(() => {
    if (!token) return;

    const handleOnline = () => {
      if (isUsersDataStale()) {
        loadData(true, false); // Don't reset page on reconnect
      }
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [token, loadData]);

  // Load more users (pagination)
  const loadMore = useCallback(() => {
    if (!token) return;
    
    const pagination = getUsersPagination();
    if (pagination.currentPage < pagination.totalPages) {
      userDataService.setFilters({ page: pagination.currentPage + 1 });
      loadData(false, false); // Don't reset page
    }
  }, [token, loadData]);

  // Go to specific page (client-side only)
  const goToPage = useCallback((page: number) => {
    if (!token) return;
    
    const pagination = getUsersPagination();
    if (page >= 1 && page <= pagination.totalPages && page !== currentPage) {
      setCurrentPage(page); // Update local state to trigger re-render
      // Don't call userDataService.setFilters() - we're doing client-side pagination!
    }
  }, [token, currentPage]);

  // Set filter and reload - this should search ALL users
  const setFilter = useCallback(async (email?: string, userId?: string, membersOnly?: boolean) => {
    if (!token) return;
    
    // Get current filters to preserve existing membersOnly state when searching
    const currentFilters = userDataService.getFilters();
    
    setCurrentPage(1); // Reset to page 1 when searching
    userDataService.setFilters({
      email: email || null,
      userId: userId || null,
      page: 1,
      membersOnly: membersOnly !== undefined ? membersOnly : currentFilters.membersOnly,
      includeUsageData: options.includeUsageData
    });
    
    // For search/filter, we want to load all matching users
    await loadData(false, true);
    
    // Then start background loading of additional pages for better search experience
    setTimeout(() => {
      prefetchAllUsers();
    }, 500);
  }, [token, loadData, prefetchAllUsers, options.includeUsageData]);

  // Refresh function
  const refresh = useCallback(() => {
    if (!token) return;
    loadData(true, false, true); // Force refresh regardless of staleness
  }, [token, loadData]);
  
  // Get users from the store
  const allUsers = getUsers();
  const pagination = getUsersPagination();
  
  // Client-side pagination: slice users for current page
  const usersPerPage = 10;
  const startIndex = (currentPage - 1) * usersPerPage; // Use local currentPage
  const endIndex = startIndex + usersPerPage;
  const paginatedUsers = allUsers.slice(startIndex, endIndex);

  const result: UseUsersResult = {
    users: paginatedUsers,
    allUsers,
    isLoading,
    isBackgroundLoading,
    isLoadingAllUsers: isLoadingAllUsers(),
    error,
    refresh,
    currentPage, // Return local currentPage
    totalPages: pagination.totalPages, // Use API totalPages for nav display
    hasMore: currentPage < pagination.totalPages, // Use local currentPage
    loadMore,
    goToPage,
    setFilter
  };

  return result;
};

export { useUsers };
export default useUsers;
